import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, ShieldCheck, Target, Radar, Radio, HeartHandshake, Scale, Phone, Mail } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { mediaUrl, waHref, telHref } from "@/lib/api";
import { SocialLinks, WorkingHours } from "@/components/site/Social";
import { Seo, businessLd } from "@/components/site/Seo";
import { CtaBand, ServiceCard } from "@/components/site/Common";

const WHY_ICONS = [ShieldCheck, Target, Radar, Radio, HeartHandshake, Scale];

function Hero({ s }) {
  return (
    <section className="grain relative isolate overflow-hidden bg-[color:var(--ink)] text-white" data-testid="home-hero">
      <img src={mediaUrl(s.hero_image) || "/img/hero.webp"} alt="Otel girişinde görev yapan özel güvenlik görevlisi" className="absolute inset-0 -z-10 h-full w-full object-cover object-[70%_center]" fetchPriority="high" />
      <div className="hero-shade absolute inset-0 -z-10" />
      <div className="container-x flex min-h-[78vh] flex-col justify-center py-20 sm:min-h-[82vh]">
        <span className="eyebrow rise">Kuşadası · Aydın · Ege Bölgesi</span>
        <h1 className="rise rise-1 mt-5 max-w-3xl text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-title">
          {s.hero_title}
        </h1>
        <p className="rise rise-2 mt-6 max-w-xl text-base leading-relaxed text-zinc-300 sm:text-lg" data-testid="hero-subtitle">{s.hero_subtitle}</p>
        <div className="rise rise-3 mt-9 flex flex-col gap-3 sm:flex-row">
          <Link to="/teklif-al" className="btn-red !px-7 !py-4 text-base" data-testid="hero-quote-btn">
            Güvenlik Hizmeti Teklifi Al <ArrowRight className="h-5 w-5" />
          </Link>
          <a href={waHref(s)} target="_blank" rel="noopener noreferrer" className="btn-ghost-dark !px-7 !py-4 text-base" data-testid="hero-whatsapp-btn">
            <MessageCircle className="h-5 w-5" /> WhatsApp ile İletişime Geç
          </a>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 h-1 w-1/3 bg-[color:var(--red)]" />
    </section>
  );
}

function Intro({ s }) {
  return (
    <section className="bg-white py-20 sm:py-24" data-testid="home-intro">
      <div className="container-x grid items-center gap-12 lg:grid-cols-2">
        <div>
          <span className="eyebrow">Biz Kimiz</span>
          <h2 className="mt-4 text-2xl font-extrabold leading-tight text-zinc-950 sm:text-3xl lg:text-4xl">{s.intro_title}</h2>
          {(s.intro_text || "").split(/\n\s*\n/).map((p) => (
            <p key={p} className="mt-4 leading-relaxed text-zinc-600">{p}</p>
          ))}
          <Link to="/hakkimizda" className="btn-outline mt-8" data-testid="intro-about-btn">Hakkımızda <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="relative">
          <div className="absolute -left-3 -top-3 h-24 w-24 border-l-4 border-t-4 border-[color:var(--red)]" />
          <video controls preload="none" poster={mediaUrl(s.video_poster) || "/img/hero.webp"} className="relative aspect-video w-full rounded-lg bg-black shadow-2xl" data-testid="intro-video">
            <source src={mediaUrl(s.video_url)} type="video/mp4" />
          </video>
          <p className="mt-3 text-xs text-zinc-500">Rüzgar Özel Güvenlik tanıtım videosu (sesli)</p>
        </div>
      </div>
    </section>
  );
}

function Why({ items = [] }) {
  return (
    <section className="bg-[color:var(--ink)] py-20 text-white sm:py-24" data-testid="home-why">
      <div className="container-x">
        <span className="eyebrow">Yaklaşımımız</span>
        <h2 className="mt-4 max-w-2xl text-2xl font-extrabold sm:text-3xl lg:text-4xl">Neden Rüzgar Özel Güvenlik?</h2>
        <div className="mt-12 grid gap-px overflow-hidden rounded-lg border border-[color:var(--line-dark)] bg-[color:var(--line-dark)] sm:grid-cols-2 lg:grid-cols-3">
          {items.map((w, i) => {
            const I = WHY_ICONS[i % WHY_ICONS.length];
            return (
              <div key={w.title} className="group bg-[color:var(--slate)] p-7 transition-colors hover:bg-[color:var(--card-dark)]" data-testid={`why-item-${i}`}>
                <I className="h-8 w-8 text-[color:var(--red)] transition-transform group-hover:scale-110" />
                <h3 className="mt-5 text-lg font-bold">{w.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">{w.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function SectorStrip({ sectors }) {
  return (
    <section className="bg-zinc-50 py-20 sm:py-24" data-testid="home-sectors">
      <div className="container-x">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <span className="eyebrow">Sektörler ve Bölgeler</span>
            <h2 className="mt-4 text-2xl font-extrabold text-zinc-950 sm:text-3xl lg:text-4xl">Hizmet verdiğimiz alanlar</h2>
          </div>
          <Link to="/sektorler" className="btn-outline" data-testid="home-all-sectors-btn">Tümünü gör <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {sectors.map((sc) => (
            <Link key={sc.slug} to={`/hizmetler/${sc.slug}`} className="group relative flex aspect-[4/3] items-end overflow-hidden rounded-lg bg-zinc-900 sm:aspect-[5/4]" data-testid={`sector-card-${sc.slug}`}>
              <img src={mediaUrl(sc.image)} alt={sc.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-70 transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="relative p-6 text-white">
                <h3 className="text-lg font-bold">{sc.title}</h3>
                <p className="mt-1 line-clamp-2 text-sm text-zinc-300">{sc.short}</p>
              </div>
              <span className="absolute left-0 top-0 h-1 w-0 bg-[color:var(--red)] transition-[width] duration-300 group-hover:w-full" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactStrip({ s }) {
  const item = (Icon, label, value, href, testid, ext) => (
    <a href={href} target={ext ? "_blank" : undefined} rel="noopener noreferrer" className="group flex items-center gap-3" data-testid={testid}>
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-[color:var(--red)] text-white"><Icon className="h-5 w-5" /></span>
      <span className="min-w-0"><span className="block text-xs uppercase tracking-widest text-zinc-400">{label}</span><span className="block truncate text-sm font-semibold text-white group-hover:underline">{value}</span></span>
    </a>
  );
  return (
    <section className="border-t border-white/10 bg-[color:var(--slate)] py-12" data-testid="home-contact-strip">
      <div className="container-x grid gap-8 md:grid-cols-2 lg:grid-cols-4 lg:items-center">
        {item(Phone, "Telefon", s.phone, telHref(s.phone), "home-contact-phone")}
        {s.email && item(Mail, "E-posta", s.email, `mailto:${s.email}`, "home-contact-email")}
        {item(MessageCircle, "WhatsApp", "Hemen yazın", waHref(s), "home-contact-whatsapp", true)}
        <div>
          <p className="mb-2 text-xs uppercase tracking-widest text-zinc-400">Bizi takip edin</p>
          <SocialLinks testid="home-social" />
        </div>
        {(s.working_hours || []).length > 0 && <div className="md:col-span-2 lg:col-span-1"><WorkingHours dark /></div>}
      </div>
    </section>
  );
}

export default function Home() {
  const { settings: s, services, sectors } = useSite();
  return (
    <>
      <Seo title={s.seo?.home?.title} description={s.seo?.home?.description} path="/" jsonLd={s.legal_name ? businessLd(s) : null} />
      <Hero s={s} />
      <Intro s={s} />
      <section className="bg-zinc-50 py-20 sm:py-24" data-testid="home-services">
        <div className="container-x">
          <span className="eyebrow">Hizmetlerimiz</span>
          <h2 className="mt-4 max-w-2xl text-2xl font-extrabold text-zinc-950 sm:text-3xl lg:text-4xl">İhtiyaca göre planlanan özel güvenlik hizmetleri</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((sv, i) => <ServiceCard key={sv.slug} item={sv} i={i} />)}
          </div>
        </div>
      </section>
      <Why items={s.why_items} />
      <SectorStrip sectors={sectors} />
      <CtaBand />
      <ContactStrip s={s} />
    </>
  );
}
